import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import adminService from '../services/adminService';

const useAdminStore = create(
  persist(
    (set, get) => ({
  // State
  isAdmin: false,
  adminToken: null,
  adminUser: null,
  needsAdminLogin: false,
  isLoading: false,
  error: null,
  
      // Dashboard data
      dashboardStats: null,
      users: [],
      orders: [],
      withdrawals: [],
      deposits: [],

      pairs: [],
      pairSettings: [],
      disabledCoins: [],
      feesAndLimits: [],
      botConfigs: [],
      notifications: [],

      // Coins data
      coinsData: null,
      coinsStatus: null,
      limitsData: null,

      // Wallet transactions (deposits/withdrawals)
      walletTransactions: [],
      walletTransactionsPagination: { page: 1, pageSize: 20, total: 0 },
      pendingTransactionsSummary: null,

      // Additional limit-related data
      withdrawalLimitLevels: [],
      withdrawalLimitLevelsPagination: { page: 1, pageSize: 20, total: 0 },
      userExchangeFees: [],
      userExchangeFeesPagination: { page: 1, pageSize: 20, total: 0 },
      withdrawalFees: [],
      withdrawalFeesPagination: { page: 1, pageSize: 20, total: 0 },

      // Keeper management data
      keeperDetails: {},
      keeperBalances: {},
      keeperAccumulationStatus: {},
      keeperWalletAddresses: {},

      // Deposit management data
      depositInspection: {},
      depositAccumulationStatus: {},

      // Withdrawal management data
      withdrawalInspection: {},
      withdrawalLimits: {},

      // Quick swap management data
      quickSwapStatus: {},
      btc1Price: null,

  // System management data
  systemStatus: {},
  accumulationLimits: {},

  // Additional management data
  btc1DepositStatus: {},
  withdrawalDetails: {},
  keeperStatus: {},
  allWalletsWithFunds: {},
  accumulationTransactions: {},
  depositTransactionsDetailed: {},

      // Pagination
      usersPagination: { page: 1, pageSize: 20, total: 0 },
      ordersPagination: { page: 1, pageSize: 20, total: 0 },
      withdrawalsPagination: { page: 1, pageSize: 20, total: 0 },
      pairsPagination: { page: 1, pageSize: 20, total: 0 },
      pairSettingsPagination: { page: 1, pageSize: 20, total: 0 },
      disabledCoinsPagination: { page: 1, pageSize: 20, total: 0 },

      // Actions
  checkAdminStatus: (userEmail) => {
    const isAdmin = adminService.isAdmin(userEmail);
    const adminToken = adminService.getAdminToken();
    const adminUser = JSON.parse(localStorage.getItem('admin_user') || 'null');
    
    set({ 
      isAdmin, 
      adminToken, 
      adminUser,
      needsAdminLogin: isAdmin && !adminToken // User is admin but needs to log in
    });
  },

      adminLogin: async (credentials) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.adminLogin(credentials);
          set({ 
            adminToken: response.access,
            adminUser: response.user || {},
            isAdmin: true,
            needsAdminLogin: false,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Admin login failed', 
            isLoading: false,
            isAdmin: false,
            adminToken: null,
            adminUser: null
          });
          throw error;
        }
      },

      adminLogout: () => {
        adminService.adminLogout();
        set({ 
          isAdmin: false,
          adminToken: null,
          adminUser: null,
          error: null
        });
      },

      // ===== DASHBOARD DATA =====

      loadDashboardStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const [stats, tradeVolume, inouts, depositsWithdrawals] = await Promise.all([
            adminService.getDashboardStats(),
            adminService.getTradeVolumeStats(),
            adminService.getCommonInoutsStats(),
            adminService.getDepositsWithdrawalsStats()
          ]);

          set({ 
            dashboardStats: {
              users: stats,
              tradeVolume,
              inouts,
              depositsWithdrawals
            },
            isLoading: false,
            error: null 
          });
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load dashboard stats', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== USER MANAGEMENT =====

      loadUsers: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getUsers(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            users: response.results || response,
            usersPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load users', 
            isLoading: false 
          });
          throw error;
        }
      },

      searchUsers: async (searchTerm, params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.searchUsers(searchTerm, params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            users: response.results || response,
            usersPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to search users', 
            isLoading: false 
          });
          throw error;
        }
      },

      topupUser: async (userIds, amount, currency, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.topupUser(userIds, amount, currency, password);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to top up user', 
            isLoading: false 
          });
          throw error;
        }
      },

      getUserBalances: async (userId, userEmail) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getUserBalances(userId, userEmail);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to get user balances', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateUser: async (userId, changes) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateUser(userId, changes);
          set((state) => ({
            users: state.users.map((user) => user.id === userId ? { ...user, ...response } : user),
            isLoading: false,
            error: null,
          }));
          return response;
        } catch (error) {
          set({ error: error.message || 'Failed to update user', isLoading: false });
          throw error;
        }
      },

      // ===== ORDERS MANAGEMENT =====

      loadOrders: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getAllOrders(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            orders: response.results || response,
            ordersPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load orders', 
            isLoading: false 
          });
          throw error;
        }
      },

      cancelOrder: async (orderId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.cancelOrder(orderId);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to cancel order', 
            isLoading: false 
          });
          throw error;
        }
      },

      revertOrders: async (orderIds) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.revertOrders(orderIds);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to revert orders', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== WITHDRAWALS MANAGEMENT =====

      loadWithdrawals: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getWithdrawalRequests(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            withdrawals: response.results || response,
            withdrawalsPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load withdrawals', 
            isLoading: false 
          });
          throw error;
        }
      },

      approveWithdrawal: async (withdrawalId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.approveWithdrawal(withdrawalId);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to approve withdrawal', 
            isLoading: false 
          });
          throw error;
        }
      },

      cancelWithdrawal: async (withdrawalId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.cancelWithdrawal(withdrawalId);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to cancel withdrawal', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== COINS & PAIRS MANAGEMENT =====

      loadDisabledCoins: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getDisabledCoins(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            disabledCoins: response.results || response,
            disabledCoinsPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load disabled coins', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Load coins data from public API
      loadCoinsData: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getCoinsData();
          set({ 
            coinsData: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load coins data', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Load coins status
      loadCoinsStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getCoinsStatus();
          set({ 
            coinsStatus: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load coins status', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Load limits data from public API
      loadLimits: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getLimits();
          set({ 
            limitsData: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load limits data', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Load wallet transactions with pagination
      loadWalletTransactions: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getWalletTransactions(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            walletTransactions: response.results || response,
            walletTransactionsPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load wallet transactions', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Load pending transactions summary
      loadPendingTransactionsSummary: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getPendingTransactionsSummary();
          set({ 
            pendingTransactionsSummary: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load pending transactions summary', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Force deposit and accumulate
      forceDepositAndAccumulate: async (transactionIds) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.forceDepositAndAccumulate(transactionIds);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to force deposit and accumulate', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Revert transactions
      revertTransactions: async (transactionIds) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.revertTransactions(transactionIds);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to revert transactions', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Recheck KYT scoring
      recheckKYT: async (transactionIds) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.recheckKYT(transactionIds);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to recheck KYT', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Set external accumulation address
      setExternalAccumulation: async (transactionIds, externalAddress) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.setExternalAccumulation(transactionIds, externalAddress);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to set external accumulation address', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Trigger deposit check
      triggerDepositCheck: async (currency = null, userId = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.triggerDepositCheck(currency, userId);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to trigger deposit check', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Create disabled coin
      createDisabledCoin: async (coinData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createDisabledCoin(coinData);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create disabled coin', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Create pair
      createPair: async (pairData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createPair(pairData);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create pair', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Create pair settings
      createPairSettings: async (settingsData) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createPairSettings(settingsData);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create pair settings', 
            isLoading: false 
          });
          throw error;
        }
      },

      // Update pair settings
      updatePairSettings: async (settingsId, settings) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updatePairSettings(settingsId, settings);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update pair settings', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateDisabledCoin: async (coinId, settings) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateDisabledCoin(coinId, settings);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update disabled coin', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadPairs: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getPairs(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            pairs: response.results || response,
            pairsPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load pairs', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadPairSettings: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getPairSettings(params);
          
          // Calculate current page from offset and pageSize
          const pageSize = params.pageSize || 20;
          const currentPage = params.page || 1;
          
          set({ 
            pairSettings: response.results || response,
            pairSettingsPagination: {
              page: currentPage,
              pageSize: pageSize,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load pair settings', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== FEES & LIMITS MANAGEMENT =====

      loadFeesAndLimits: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getFeesAndLimits();
          set({ 
            feesAndLimits: response.results || response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load fees and limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateFeesAndLimits: async (limitId, settings) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateFeesAndLimits(limitId, settings);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update fees and limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== WITHDRAWAL LIMIT LEVELS MANAGEMENT =====

      loadWithdrawalLimitLevels: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getWithdrawalLimitLevels(params);
          set({ 
            withdrawalLimitLevels: response.results || response,
            withdrawalLimitLevelsPagination: {
              page: params.page || 1,
              pageSize: params.pageSize || 20,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load withdrawal limit levels', 
            isLoading: false 
          });
          throw error;
        }
      },

      createWithdrawalLimitLevel: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createWithdrawalLimitLevel(data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create withdrawal limit level', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateWithdrawalLimitLevel: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateWithdrawalLimitLevel(id, data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update withdrawal limit level', 
            isLoading: false 
          });
          throw error;
        }
      },

      deleteWithdrawalLimitLevel: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.deleteWithdrawalLimitLevel(id);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to delete withdrawal limit level', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== USER EXCHANGE FEES MANAGEMENT =====

      loadUserExchangeFees: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getUserExchangeFees(params);
          set({ 
            userExchangeFees: response.results || response,
            userExchangeFeesPagination: {
              page: params.page || 1,
              pageSize: params.pageSize || 20,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load user exchange fees', 
            isLoading: false 
          });
          throw error;
        }
      },

      createUserExchangeFee: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createUserExchangeFee(data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create user exchange fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateUserExchangeFee: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateUserExchangeFee(id, data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update user exchange fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      deleteUserExchangeFee: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.deleteUserExchangeFee(id);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to delete user exchange fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== WITHDRAWAL FEES MANAGEMENT =====

      loadWithdrawalFees: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getWithdrawalFees(params);
          set({ 
            withdrawalFees: response.results || response,
            withdrawalFeesPagination: {
              page: params.page || 1,
              pageSize: params.pageSize || 20,
              total: response.count || response.length || 0
            },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load withdrawal fees', 
            isLoading: false 
          });
          throw error;
        }
      },

      createWithdrawalFee: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createWithdrawalFee(data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create withdrawal fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateWithdrawalFee: async (id, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateWithdrawalFee(id, data);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update withdrawal fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      deleteWithdrawalFee: async (id) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.deleteWithdrawalFee(id);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to delete withdrawal fee', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== KEEPER MANAGEMENT ACTIONS =====

      loadKeeperDetails: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getKeeperDetails(currency);
          set({ 
            keeperDetails: { ...get().keeperDetails, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load keeper details', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateKeeperWallet: async (currency, address, privateKey) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateKeeperWallet(currency, address, privateKey);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update keeper wallet', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadKeeperBalance: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getKeeperBalance(currency);
          set({ 
            keeperBalances: { ...get().keeperBalances, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load keeper balance', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadKeeperAccumulationStatus: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getKeeperAccumulationStatus(currency);
          set({ 
            keeperAccumulationStatus: { ...get().keeperAccumulationStatus, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load keeper accumulation status', 
            isLoading: false 
          });
          throw error;
        }
      },

      triggerKeeperAccumulation: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.triggerKeeperAccumulation(currency);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to trigger keeper accumulation', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadKeeperWalletAddresses: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.checkKeeperWalletAddresses(currency);
          set({ 
            keeperWalletAddresses: { ...get().keeperWalletAddresses, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load keeper wallet addresses', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== DEPOSIT MANAGEMENT ACTIONS =====

      approveAllPendingDeposits: async (currency = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.approveAllPendingDeposits(currency);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to approve pending deposits', 
            isLoading: false 
          });
          throw error;
        }
      },

      inspectDepositTransactions: async (currency, address = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.inspectDepositTransactions(currency, address);
          set({ 
            depositInspection: { ...get().depositInspection, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to inspect deposit transactions', 
            isLoading: false 
          });
          throw error;
        }
      },

      checkDepositAccumulation: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.checkDepositAccumulation(currency);
          set({ 
            depositAccumulationStatus: { ...get().depositAccumulationStatus, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to check deposit accumulation', 
            isLoading: false 
          });
          throw error;
        }
      },

      fixDepositAccumulation: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.fixDepositAccumulation(currency);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to fix deposit accumulation', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== WITHDRAWAL MANAGEMENT ACTIONS =====

      processBTC1Payouts: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.processBTC1Payouts();
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to process BTC1 payouts', 
            isLoading: false 
          });
          throw error;
        }
      },

      inspectWithdrawals: async (currency, state = null) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.inspectWithdrawals(currency, state);
          set({ 
            withdrawalInspection: { ...get().withdrawalInspection, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to inspect withdrawals', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateWithdrawalLimits: async (level, amount) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateWithdrawalLimits(level, amount);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update withdrawal limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== QUICK SWAP MANAGEMENT ACTIONS =====

      addQuickSwapLiquidity: async (currency, amount, type) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.addQuickSwapLiquidity(currency, amount, type);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to add quick swap liquidity', 
            isLoading: false 
          });
          throw error;
        }
      },

      removeQuickSwapLiquidity: async (currency, amount, type) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.removeQuickSwapLiquidity(currency, amount, type);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to remove quick swap liquidity', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadQuickSwapStatus: async (currency = 'ALL') => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getQuickSwapStatus(currency);
          set({ 
            quickSwapStatus: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load quick swap status', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateBTC1Price: async (price) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateBTC1Price(price);
          set({ 
            btc1Price: price,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update BTC1 price',
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== SYSTEM MANAGEMENT ACTIONS =====

      seedLimits: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.seedLimits();
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to seed limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      loadSystemStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getSystemStatus();
          set({ 
            systemStatus: response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load system status', 
            isLoading: false 
          });
          throw error;
        }
      },

      checkAccumulationLimits: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.checkAccumulationLimits(currency);
          set({ 
            accumulationLimits: { ...get().accumulationLimits, [currency]: response },
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to check accumulation limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      fixAccumulationLimits: async (currency, limit) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.fixAccumulationLimits(currency, limit);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to fix accumulation limits', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== BOT MANAGEMENT =====

      loadBotConfigs: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getBotConfigs();
          set({ 
            botConfigs: response.results || response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load bot configs', 
            isLoading: false 
          });
          throw error;
        }
      },

      updateBotConfig: async (botId, config) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.updateBotConfig(botId, config);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to update bot config', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== NOTIFICATIONS =====

      loadNotifications: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.getNotifications();
          set({ 
            notifications: response.results || response,
            isLoading: false,
            error: null 
          });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to load notifications', 
            isLoading: false 
          });
          throw error;
        }
      },

      createNotification: async (notification) => {
        set({ isLoading: true, error: null });
        try {
          const response = await adminService.createNotification(notification);
          set({ isLoading: false, error: null });
          return response;
        } catch (error) {
          set({ 
            error: error.message || 'Failed to create notification', 
            isLoading: false 
          });
          throw error;
        }
      },

      // ===== UTILITY FUNCTIONS =====

      clearError: () => {
        set({ error: null });
      },

      setLoading: (loading) => {
        set({ isLoading: loading });
      },

      // ===== KEEPER MANAGEMENT =====

      // Load keepers
      loadKeepers: async () => {
        set({ isLoading: true, error: null });
        try {
          const keepers = await adminService.getKeepers();
          set({ keepers, isLoading: false, error: null });
        } catch (error) {
          set({ error: error.message || 'Failed to load keepers', isLoading: false });
        }
      },

      // Load keeper accumulation limits
      loadKeeperAccumulationLimits: async (currency) => {
        try {
          const limits = await adminService.getKeeperAccumulationLimits(currency);
          set(state => ({
            keeperAccumulationLimits: { ...state.keeperAccumulationLimits, [currency]: limits }
          }));
        } catch (error) {
          console.error('Failed to load keeper accumulation limits:', error);
        }
      },

      // Update keeper
      updateKeeper: async (keeperId, settings) => {
        set({ isLoading: true, error: null });
        try {
          const updatedKeeper = await adminService.updateKeeper(keeperId, settings);
          set(state => ({
            keepers: state.keepers.map(k => k.id === keeperId ? updatedKeeper : k),
            isLoading: false,
            error: null
          }));
          return updatedKeeper;
        } catch (error) {
          set({ error: error.message || 'Failed to update keeper', isLoading: false });
          throw error;
        }
      },

      // Accumulate ALL user wallet funds to keeper
      accumulateAllToKeeper: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.accumulateAllToKeeper(currency);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to accumulate all to keeper', isLoading: false });
          throw error;
        }
      },

      // Withdraw from keeper to external address
      withdrawFromKeeper: async (currency, toAddress, amount) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.withdrawFromKeeper(currency, toAddress, amount);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to withdraw from keeper', isLoading: false });
          throw error;
        }
      },

      // Update keeper accumulation limits
      updateKeeperAccumulationLimits: async (currency, limits) => {
        set({ isLoading: true, error: null });
        try {
          const updatedLimits = await adminService.updateKeeperAccumulationLimits(currency, limits);
          set(state => ({
            keeperAccumulationLimits: { ...state.keeperAccumulationLimits, [currency]: updatedLimits },
            isLoading: false,
            error: null
          }));
          return updatedLimits;
        } catch (error) {
          set({ error: error.message || 'Failed to update keeper accumulation limits', isLoading: false });
          throw error;
        }
      },

      // Refresh all data
      refreshAllData: async () => {
        const { 
          loadDashboardStats, loadUsers, loadOrders, loadWithdrawals, loadDisabledCoins, 
          loadPairs, loadPairSettings, loadCoinsData, loadCoinsStatus, loadLimits, 
          loadWalletTransactions, loadPendingTransactionsSummary, loadFeesAndLimits, 
          loadWithdrawalLimitLevels, loadUserExchangeFees, loadWithdrawalFees, 
          loadKeeperDetails, loadKeeperBalance, loadKeeperAccumulationStatus,
          loadDepositAccumulation, loadWithdrawalInspection, loadQuickSwapStatus,
          loadSystemStatus, loadBotConfigs, loadNotifications, loadKeepers 
        } = get();
        
        try {
          await Promise.all([
            loadDashboardStats(),
            loadUsers(),
            loadOrders(),
            loadWithdrawals(),
            loadDisabledCoins(),
            loadPairs(),
            loadPairSettings(),
            loadCoinsData(),
            loadCoinsStatus(),
            loadLimits(),
            loadWalletTransactions({ page: 1, pageSize: 20 }),
            loadPendingTransactionsSummary(),
            loadFeesAndLimits(),
            loadWithdrawalLimitLevels({ page: 1, pageSize: 20 }),
            loadUserExchangeFees({ page: 1, pageSize: 20 }),
            loadWithdrawalFees({ page: 1, pageSize: 20 }),
            loadKeeperDetails('BTC1'),
            loadKeeperBalance('BTC1'),
            loadKeeperAccumulationStatus('BTC1'),
            loadDepositAccumulation('BTC1'),
            loadWithdrawalInspection('BTC1'),
            loadQuickSwapStatus('ALL'),
            loadSystemStatus(),
            loadBotConfigs(),
            loadNotifications(),
            loadKeepers()
          ]);
        } catch (error) {
          console.error('Failed to refresh all data:', error);
        }
      },

      // ===== ADDITIONAL MANAGEMENT ACTIONS =====
      loadBTC1DepositStatus: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.checkBTC1DepositStatus();
          set({ btc1DepositStatus: data, isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load BTC1 deposit status', isLoading: false });
          throw error;
        }
      },

      fixBTC1DepositAccumulation: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.fixBTC1DepositAccumulation();
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to fix BTC1 deposit accumulation', isLoading: false });
          throw error;
        }
      },

      loadWithdrawalDetails: async (currency = 'BTC1', state = null) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.inspectWithdrawalsDetailed(currency, state);
          set(state => ({
            withdrawalDetails: { ...state.withdrawalDetails, [currency]: data },
            isLoading: false,
            error: null
          }));
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load withdrawal details', isLoading: false });
          throw error;
        }
      },

      updateKeeperToNewWallet: async (currency, address, privateKey) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.updateKeeperToNewWallet(currency, address, privateKey);
          // Refresh keeper details after update
          get().loadKeeperDetails(currency);
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to update keeper to new wallet', isLoading: false });
          throw error;
        }
      },

      updateKeeperToOldWallet: async (currency, address) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.updateKeeperToOldWallet(currency, address);
          // Refresh keeper details after update
          get().loadKeeperDetails(currency);
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to update keeper to old wallet', isLoading: false });
          throw error;
        }
      },

      loadKeeperStatus: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.checkKeeperStatus(currency);
          set(state => ({
            keeperStatus: { ...state.keeperStatus, [currency]: data },
            isLoading: false,
            error: null
          }));
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load keeper status', isLoading: false });
          throw error;
        }
      },

      loadAllWalletsWithFunds: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.checkAllWalletsWithFunds(currency);
          set(state => ({
            allWalletsWithFunds: { ...state.allWalletsWithFunds, [currency]: data },
            isLoading: false,
            error: null
          }));
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load all wallets with funds', isLoading: false });
          throw error;
        }
      },

      loadAccumulationTransactions: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.inspectAccumulationTransactions(currency);
          set(state => ({
            accumulationTransactions: { ...state.accumulationTransactions, [currency]: data },
            isLoading: false,
            error: null
          }));
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load accumulation transactions', isLoading: false });
          throw error;
        }
      },

      loadDepositTransactionsDetailed: async (currency) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.inspectDepositTransactionsDetailed(currency);
          set(state => ({
            depositTransactionsDetailed: { ...state.depositTransactionsDetailed, [currency]: data },
            isLoading: false,
            error: null
          }));
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to load detailed deposit transactions', isLoading: false });
          throw error;
        }
      },

      // ===== MISSING APIS ACTIONS =====

      getQuickSwapLiquidityStatus: async (currency = 'ALL') => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.getQuickSwapLiquidityStatus(currency);
          set({ quickSwapStatus: { ...get().quickSwapStatus, liquidity: data }, isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to get quick swap liquidity status', isLoading: false });
          throw error;
        }
      },

      clearSystemCache: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.clearSystemCache();
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to clear system cache', isLoading: false });
          throw error;
        }
      },

      restartWorkers: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.restartWorkers();
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to restart workers', isLoading: false });
          throw error;
        }
      },

      backupDatabase: async () => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.backupDatabase();
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to backup database', isLoading: false });
          throw error;
        }
      },

      toggleMaintenanceMode: async (enabled) => {
        set({ isLoading: true, error: null });
        try {
          const data = await adminService.toggleMaintenanceMode(enabled);
          set({ isLoading: false, error: null });
          return data;
        } catch (error) {
          set({ error: error.message || 'Failed to toggle maintenance mode', isLoading: false });
          throw error;
        }
      },

      // ===== ADDITIONAL MANAGEMENT COMMANDS =====

      manageWithdrawalLimits: async (action, data = {}) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.manageWithdrawalLimits(action, data);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to manage withdrawal limits', isLoading: false });
          throw error;
        }
      },

      manageQuickSwapLiquidity: async (action, currency, amount = null, type = 'liquidity') => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.manageQuickSwapLiquidity(action, currency, amount, type);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to manage quick swap liquidity', isLoading: false });
          throw error;
        }
      },

      disableUsersOrders: async (emails, disable = true) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.disableUsersOrders(emails, disable);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to disable users orders', isLoading: false });
          throw error;
        }
      },

      disableUsersWithdrawals: async (emails, disable = true) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.disableUsersWithdrawals(emails, disable);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to disable users withdrawals', isLoading: false });
          throw error;
        }
      },

      disableUsersTopups: async (emails, disable = true) => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.disableUsersTopups(emails, disable);
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to disable users topups', isLoading: false });
          throw error;
        }
      },

      getBTC1Price: async () => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.getBTC1Price();
          set({ btc1Price: result.price, isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to get BTC1 price', isLoading: false });
          throw error;
        }
      },

      refreshOrderbook: async () => {
        set({ isLoading: true, error: null });
        try {
          const result = await adminService.refreshOrderbook();
          set({ isLoading: false, error: null });
          return result;
        } catch (error) {
          set({ error: error.message || 'Failed to refresh orderbook', isLoading: false });
          throw error;
        }
      }
    }
  ),
    {
      name: 'admin-storage',
      partialize: (state) => ({ 
        isAdmin: state.isAdmin,
        adminToken: state.adminToken,
        adminUser: state.adminUser
      }),
    }
));

export default useAdminStore;
